import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { LocalStorageService } from '@/services/storage/local-storage.service'

export type ThemeMode = 'light' | 'dark' | 'system'

export const useThemeStore = defineStore('theme', () => {
  const storage = new LocalStorageService()
  const theme = ref<ThemeMode>((storage.getTheme() as ThemeMode) || 'light')
  const isDark = ref(false)

  function applyTheme(mode: ThemeMode) {
    let shouldBeDark = false

    if (mode === 'system') {
      shouldBeDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    } else {
      shouldBeDark = mode === 'dark'
    }

    isDark.value = shouldBeDark

    if (shouldBeDark) {
      document.documentElement.classList.add('dark')
      document.documentElement.setAttribute('data-theme', 'dark')
    } else {
      document.documentElement.classList.remove('dark')
      document.documentElement.setAttribute('data-theme', 'light')
    }
  }

  function setTheme(mode: ThemeMode) {
    theme.value = mode
    storage.setTheme(mode)
    applyTheme(mode)
  }

  function toggleTheme() {
    const newTheme = isDark.value ? 'light' : 'dark'
    setTheme(newTheme)
  }

  function initializeTheme() {
    const savedTheme = storage.getTheme() as ThemeMode
    if (savedTheme) {
      theme.value = savedTheme
    }
    applyTheme(theme.value)

    // Listen for system theme changes
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
      if (theme.value === 'system') {
        applyTheme('system')
      }
    })
  }

  return {
    theme,
    isDark,
    setTheme,
    toggleTheme,
    initializeTheme,
  }
})
